import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Users, Stethoscope, CalendarDays, ArrowRight, User2,
  Activity, FileText, LineChart, Sparkles, ChevronRight,
  BookOpen, BellRing, Settings as SettingsIcon, Link as LinkIcon, ExternalLink,
} from "lucide-react";
import { Patients, Overview } from "@/lib/api";
import { useDraft, readDraft, clearDraft } from "@/lib/draft";

const EMPTY_PATIENT = { name: "", age: "", gender: "", occupation: "", email: "", notes: "", clinical_history: "" };
import { useSetting } from "@/lib/appSettings";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function scoreColor(score) {
  if (score == null) return { text: "text-slate-400", glow: "rgba(148,163,184,0.3)", stroke: "#94a3b8" };
  if (score >= 80) return { text: "text-emerald-300", glow: "rgba(52,211,153,0.55)", stroke: "#34d399" };
  if (score >= 60) return { text: "text-amber-300", glow: "rgba(251,191,36,0.55)", stroke: "#fbbf24" };
  return { text: "text-rose-300", glow: "rgba(244,63,94,0.55)", stroke: "#f43f5e" };
}

function statusBadge(status) {
  const map = {
    "Em acompanhamento": "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    "Avaliado":      "border-sky-500/30 bg-sky-500/10 text-sky-200",
    "Novo":          "border-slate-600/40 bg-slate-800/40 text-slate-300",
  };
  return map[status] || map["Novo"];
}

/* ---------- Hero silhouette (subtle SVG with neon alignment lines) ---------- */
function HeroVisual() {
  return (
    <div className="absolute inset-y-0 right-0 w-[320px] md:w-[420px] pointer-events-none opacity-60 hidden md:block">
      <svg viewBox="0 0 200 240" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="dash-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <linearGradient id="dash-plumb" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="rgba(56,189,248,0)" />
            <stop offset="18%" stopColor="rgba(56,189,248,0.55)" />
            <stop offset="82%" stopColor="rgba(52,211,153,0.4)" />
            <stop offset="100%" stopColor="rgba(52,211,153,0)" />
          </linearGradient>
        </defs>
        {/* silhouette */}
        <path
          d="M 100,22 Q 88,22 88,36 Q 88,46 100,46 Q 112,46 112,36 Q 112,22 100,22 Z
             M 76,56 L 124,56 L 132,90 Q 138,96 134,108 L 128,116 L 124,150 L 116,210 L 104,228 L 96,228 L 84,210 L 76,150 L 72,116 L 66,108 Q 62,96 68,90 Z"
          fill="none" stroke="rgba(56,189,248,0.45)" strokeWidth="1.2" strokeLinejoin="round"
          filter="url(#dash-glow)"
        />
        {/* plumb line */}
        <rect x="97" y="8" width="6" height="224" fill="url(#dash-plumb)" opacity="0.8" filter="url(#dash-glow)" />
        <line x1="100" y1="10" x2="100" y2="232" stroke="rgba(125,211,252,0.7)" strokeWidth="0.3" strokeDasharray="1 2.5" />
        {/* horizontal guides */}
        {[56, 108, 158, 208].map((y, i) => (
          <line key={i} x1="50" y1={y} x2="150" y2={y} stroke="rgba(52,211,153,0.35)" strokeWidth="0.4" />
        ))}
        {/* landmark dots */}
        {[[100,22],[76,56],[124,56],[72,116],[128,116],[84,210],[116,210]].map(([cx,cy],i) => (
          <g key={i} filter="url(#dash-glow)">
            <circle cx={cx} cy={cy} r="2.4" fill="rgba(56,189,248,0.2)" />
            <circle cx={cx} cy={cy} r="1.1" fill="#7dd3fc" />
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ---------- Stat card ---------- */
function StatCard({ label, value, icon: Icon, accent = "sky", testId, to }) {
  const accentMap = {
    sky: "border-sky-500/30 hover:border-sky-400/50 bg-sky-500/5 text-sky-300",
    emerald: "border-emerald-500/30 hover:border-emerald-400/50 bg-emerald-500/5 text-emerald-300",
    violet: "border-violet-500/30 hover:border-violet-400/50 bg-violet-500/5 text-violet-300",
  };
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
          <div className="mt-3 font-display text-4xl text-slate-50">{value}</div>
        </div>
        <Icon className="w-6 h-6 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition" strokeWidth={1.5} />
      </div>
      <div className="absolute inset-x-6 bottom-3 h-[2px] opacity-0 group-hover:opacity-60 transition"
           style={{ background: `linear-gradient(90deg, transparent, currentColor, transparent)` }} />
    </>
  );
  const baseCls = `group relative rounded-2xl border p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.99] ${accentMap[accent]}`;
  const baseStyle = document.documentElement.classList.contains("light") ? undefined : { boxShadow: "0 0 0 1px rgba(15,23,42,0.4), inset 0 0 40px rgba(2,6,23,0.3)" };
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={`${baseCls} cursor-pointer block`} style={baseStyle}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={baseCls} style={baseStyle}>
      {inner}
    </div>
  );
}

/* ---------- Score ring (mini, for patient cards) ---------- */
function MiniScoreRing({ score }) {
  const size = 46, stroke = 4, r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const col = scoreColor(score);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} stroke="rgba(30,41,59,0.9)" strokeWidth={stroke} fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={col.stroke} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ filter: `drop-shadow(0 0 6px ${col.glow})`, transition: "stroke-dashoffset 800ms ease" }} />
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center text-xs font-mono ${col.text}`}>
        {score == null ? "—" : Math.round(score)}
      </div>
    </div>
  );
}

/* ---------- Patient card ---------- */
function PatientCard({ p, highlight }) {
  const last = p.last_assessment;
  const col = scoreColor(last?.global_score);
  return (
    <Link
      to={`/patients/${p.id}`}
      data-testid={`patient-card-${p.id}`}
      className={`group relative rounded-2xl border transition-all duration-300 backdrop-blur-sm p-5 hover:-translate-y-0.5
        ${highlight
          ? "border-sky-500/50 bg-sky-500/5 glow-sky"
          : "border-slate-800 bg-slate-900/50 hover:border-sky-500/40 hover:bg-slate-900/80"}`}
    >
      {highlight && (
        <div className="absolute -top-2 right-4 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-sky-500 text-slate-950 font-semibold">
          Mais recente
        </div>
      )}
      <div className="flex items-start gap-4">
        <MiniScoreRing score={last?.global_score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-lg text-slate-50 truncate">{p.name}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusBadge(p.status)}`}>{p.status}</span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {p.age != null ? `${p.age} anos` : "Idade não registada"} · {p.assessments_count} {p.assessments_count === 1 ? "avaliação" : "avaliações"}
            {last?.classification && <span className={`ml-2 ${col.text}`}>· {last.classification}</span>}
          </div>
          {last?.created_at && (
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
              Última: {new Date(last.created_at).toLocaleDateString("pt-PT")}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-slate-500">{p.upcoming_appointments > 0 ? `${p.upcoming_appointments} sessão(ões) agendada(s)` : "Sem sessões agendadas"}</span>
        <span className="text-sky-400 opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
          Abrir ficha <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

/* ---------- Shortcut chip (CMS-driven) ---------- */
const ICON_MAP = {
  calendar: CalendarDays, "calendar-days": CalendarDays,
  "file-text": FileText, file: FileText,
  "book-open": BookOpen, book: BookOpen,
  bell: BellRing, "bell-ring": BellRing,
  users: Users, user: User2,
  settings: SettingsIcon,
  sparkles: Sparkles,
  link: LinkIcon, external: ExternalLink,
};

function ShortcutChip({ item }) {
  const Icon = ICON_MAP[item.icon] || ICON_MAP.link;
  const inner = (
    <div className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900/70 hover:border-sky-500/40 transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99]">
      <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-300 group-hover:bg-sky-500/20">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-100 truncate">{item.label || item.route}</div>
        {item.external && <div className="text-[10px] text-slate-500 truncate">link externo</div>}
      </div>
      {item.external
        ? <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-sky-300" />
        : <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-sky-300" />}
    </div>
  );
  if (item.external) {
    return <a href={item.route} target="_blank" rel="noreferrer" data-testid={`shortcut-${item.label}`}>{inner}</a>;
  }
  return <Link to={item.route || "/"} data-testid={`shortcut-${item.label}`}>{inner}</Link>;
}

/* ---------- Dashboard ---------- */
export default function Dashboard() {
  const nav = useNavigate();
  const heroEyebrow  = useSetting("dashboard.hero.eyebrow", "Painel clínico");
  const heroTitle    = useSetting("dashboard.hero.title", "Avaliação Funcional Osteopática");
  const heroSubtitle = useSetting("dashboard.hero.subtitle", "");
  const ctaAssess    = useSetting("dashboard.cta.new_assessment", "Nova Avaliação");
  const ctaPatient   = useSetting("dashboard.cta.new_patient", "Novo paciente");
  const lblPatients  = useSetting("dashboard.stats.patients", "Pacientes");
  const lblAssess    = useSetting("dashboard.stats.assessments", "Avaliações realizadas");
  const lblUpcoming  = useSetting("dashboard.stats.upcoming", "Próximas sessões");
  const shortcuts    = useSetting("dashboard.shortcuts", []);
  const [data, setData] = useState({ patients: [], stats: { patients: 0, assessments: 0, upcoming_appointments: 0 }, last_assessment_id: null });
  const [loading, setLoading] = useState(true);

  // new-patient dialog
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [form, setForm] = useState(() => readDraft("patient-form:new") || EMPTY_PATIENT);
  useDraft("patient-form:new", form, JSON.stringify(form) !== JSON.stringify(EMPTY_PATIENT));

  // new-assessment dialog
  const [newAssessOpen, setNewAssessOpen] = useState(false);
  const [assessPatientId, setAssessPatientId] = useState("");

  const load = async () => {
    try { setData(await Overview.get()); }
    catch { toast.error("Falha ao carregar dados"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patients = data.patients || [];
  const highlightId = data.last_assessment_id
    ? patients.find(p => p.last_assessment?.id === data.last_assessment_id)?.id
    : null;

  const patientWithMostAssessments = useMemo(() => {
    return [...patients].sort((a, b) => (b.assessments_count || 0) - (a.assessments_count || 0))[0];
  }, [patients]);

  // Pacientes elegíveis para comparação: TODOS com ≥2 avaliações, ordenados por mais recente.
  const eligibleForCompare = useMemo(() => {
    return patients
      .filter(p => (p.assessments_count || 0) >= 2)
      .sort((a, b) => {
        const da = a.last_assessment?.created_at || "";
        const db = b.last_assessment?.created_at || "";
        return db.localeCompare(da);
      });
  }, [patients]);

  const [compareOpen, setCompareOpen] = useState(false);

  const onCreatePatient = async () => {
    if (!form.name || !form.age) { toast.error("Nome e idade são obrigatórios"); return; }
    if (!form.gender) { toast.error("Seleciona o género — campo obrigatório."); return; }
    try {
      const p = await Patients.create({ ...form, age: Number(form.age) });
      toast.success("Paciente criado");
      setNewPatientOpen(false);
      setForm(EMPTY_PATIENT);
      clearDraft("patient-form:new");
      nav(`/patients/${p.id}/intake`);
    } catch {
      toast.error("Não foi possível criar o paciente");
    }
  };

  const onStartAssessment = () => {
    if (!assessPatientId) { toast.error("Escolhe um paciente"); return; }
    setNewAssessOpen(false);
    nav(`/patients/${assessPatientId}/assessments/new`);
  };

  return (
    <div className="space-y-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/70 via-slate-950/40 to-slate-950/80 p-6 md:p-10">
        <HeroVisual />
        <div className="relative max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">{heroEyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-50 mt-3 tracking-tight"
              style={{ textShadow: "0 0 28px rgba(56,189,248,0.18)" }}>
            {heroTitle}
          </h1>
          <p className="text-slate-400 mt-4 text-sm md:text-base max-w-xl">
            {heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Dialog open={newAssessOpen} onOpenChange={setNewAssessOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-assessment-btn"
                  disabled={patients.length === 0}
                  className="rounded-full px-7 py-6 font-semibold text-slate-950 transition-transform hover:-translate-y-0.5"
                  style={{
                    background: "linear-gradient(135deg, #38bdf8 0%, #22d3ee 55%, #34d399 100%)",
                    boxShadow: "0 10px 30px -8px rgba(56,189,248,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                  }}
                >
                  <Stethoscope className="w-4 h-4 mr-2" />
                  {ctaAssess}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-950 border-slate-800 text-slate-100">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Iniciar nova avaliação</DialogTitle>
                  <DialogDescription className="text-slate-400">Escolhe o paciente a avaliar.</DialogDescription>
                </DialogHeader>
                <div className="mt-2">
                  <Label className="text-slate-300">Paciente</Label>
                  <Select value={assessPatientId} onValueChange={setAssessPatientId}>
                    <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-50 mt-1" data-testid="assess-select-patient">
                      <SelectValue placeholder="Escolher paciente" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} · {p.age != null ? `${p.age} anos` : "Idade não registada"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setNewAssessOpen(false)} className="text-slate-400">Cancelar</Button>
                  <Button data-testid="start-assess-confirm" onClick={onStartAssessment}
                    className="rounded-full text-slate-950 px-6"
                    style={{ background: "linear-gradient(135deg, #38bdf8 0%, #22d3ee 55%, #34d399 100%)" }}>
                    Começar <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={newPatientOpen} onOpenChange={setNewPatientOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-patient-btn"
                  variant="outline"
                  className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800/60 hover:border-slate-500 px-5 py-6"
                >
                  <Plus className="w-4 h-4 mr-2" /> {ctaPatient}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-950 border-slate-800 text-slate-100">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Novo Paciente</DialogTitle>
                  <DialogDescription className="text-slate-400">Preenche os dados clínicos do paciente.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label htmlFor="pname" className="text-slate-300">Nome</Label>
                    <Input id="pname" data-testid="input-patient-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="page" className="text-slate-300">Idade</Label>
                      <Input id="page" type="number" data-testid="input-patient-age" value={form.age} onChange={(e) => setForm({...form, age: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="pgender" className="text-slate-300">Género <span className="text-rose-400">*</span></Label>
                      <select
                        id="pgender" data-testid="select-patient-gender"
                        value={form.gender}
                        onChange={(e) => setForm({...form, gender: e.target.value})}
                        className="w-full bg-slate-900 border border-slate-800 rounded-md text-slate-50 mt-1 px-3 py-2 text-sm h-10"
                      >
                        <option value="">Seleciona…</option>
                        <option value="masculino">Masculino</option>
                        <option value="feminino">Feminino</option>
                        <option value="neutro">Neutro</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="poccup" className="text-slate-300">Profissão</Label>
                      <Input id="poccup" data-testid="input-patient-occupation" value={form.occupation} onChange={(e) => setForm({...form, occupation: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="pemail" className="text-slate-300">Email</Label>
                      <Input id="pemail" type="email" data-testid="input-patient-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pnotes" className="text-slate-300">Notas</Label>
                    <Textarea id="pnotes" data-testid="input-patient-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="phist" className="text-slate-300">História clínica</Label>
                    <Textarea id="phist" rows={4} data-testid="input-patient-history" value={form.clinical_history} onChange={(e) => setForm({...form, clinical_history: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" className="text-slate-400" onClick={() => setNewPatientOpen(false)}>Cancelar</Button>
                  <Button data-testid="create-patient-submit" onClick={onCreatePatient} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6">Criar paciente</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 fade-in-stagger">
        <StatCard to="/patients" label={lblPatients} value={data.stats.patients} icon={Users} accent="sky" testId="stat-patients" />
        <StatCard to="/reports"  label={lblAssess}   value={data.stats.assessments} icon={Stethoscope} accent="emerald" testId="stat-assessments" />
        <StatCard to="/calendar" label={lblUpcoming} value={data.stats.upcoming_appointments} icon={CalendarDays} accent="violet" testId="stat-upcoming" />
      </section>

      {/* QUICK ACTIONS */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl text-slate-50">Atalhos rápidos</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickAction
            label="Nova Avaliação"
            sub="Iniciar mapeamento biomecânico 360º"
            icon={Sparkles}
            onClick={() => setNewAssessOpen(true)}
            disabled={patients.length === 0}
            accent="sky"
            testId="qa-new-assessment"
          />
          <QuickAction
            label="Histórico de Avaliações"
            sub="Arquivo central de relatórios funcionais"
            icon={FileText}
            onClick={() => nav("/reports")}
            accent="emerald"
            testId="qa-reports"
          />
          <QuickAction
            label="Comparar Progresso"
            sub={eligibleForCompare.length > 0
              ? `${eligibleForCompare.length} ${eligibleForCompare.length === 1 ? "paciente disponível" : "pacientes disponíveis"}`
              : "Precisas de ≥ 2 avaliações no mesmo paciente"}
            icon={LineChart}
            onClick={() => eligibleForCompare.length > 0 && setCompareOpen(true)}
            disabled={eligibleForCompare.length === 0}
            accent="violet"
            testId="qa-compare"
          />
        </div>
      </section>

      {/* Dialog: escolher paciente para comparar progresso */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100" data-testid="compare-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Comparar progresso</DialogTitle>
            <DialogDescription className="text-slate-500">
              Seleciona o paciente para abrir a vista de comparação detalhada (anterior vs atual).
            </DialogDescription>
          </DialogHeader>
          {eligibleForCompare.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">
              Nenhum paciente com avaliações suficientes para comparação.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-800 -mx-2">
              {eligibleForCompare.map((p) => (
                <button
                  key={p.id}
                  data-testid={`compare-pick-${p.id}`}
                  onClick={() => { setCompareOpen(false); nav(`/patients/${p.id}/compare`); }}
                  className="w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-900/60 transition text-left rounded-lg"
                >
                  <div className="w-9 h-9 rounded-full bg-violet-500/10 border border-violet-500/30 flex items-center justify-center shrink-0">
                    <LineChart className="w-4 h-4 text-violet-300" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-100 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {p.assessments_count} avaliações
                      {p.last_assessment?.created_at && ` · última a ${new Date(p.last_assessment.created_at).toLocaleDateString("pt-PT")}`}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CUSTOM SHORTCUTS — geridos via CMS (dashboard.shortcuts) */}
      {Array.isArray(shortcuts) && shortcuts.length > 0 && (
        <section data-testid="custom-shortcuts">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-xl text-slate-50">Ligações rápidas</h2>
            <span className="text-[11px] text-slate-500">geridas em Definições → Conteúdo</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {shortcuts.map((s, i) => <ShortcutChip key={`${s.route}-${i}`} item={s} />)}
          </div>
        </section>
      )}

      {/* PATIENTS */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-slate-50">Pacientes</h2>
          <Link to="/calendar" className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1" data-testid="go-calendar-link">
            Ver calendário <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="patients-skeleton">
            {[0, 1, 2].map(i => (
              <div key={i} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="skeleton w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-3 w-2/3" />
                    <div className="skeleton h-2 w-1/3" />
                  </div>
                </div>
                <div className="skeleton h-2 w-full" />
                <div className="skeleton h-2 w-4/5" />
              </div>
            ))}
          </div>
        ) : patients.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center bg-slate-900/30">
            <User2 className="w-10 h-10 mx-auto text-slate-600" strokeWidth={1.5} />
            <div className="mt-3 text-slate-300">Ainda não há pacientes</div>
            <div className="text-slate-500 text-sm">Cria o primeiro paciente para começar uma avaliação</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 fade-in-stagger">
            {patients.map((p) => (
              <PatientCard key={p.id} p={p} highlight={p.id === highlightId} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function QuickAction({ label, sub, icon: Icon, onClick, disabled, accent, testId, beta }) {
  const accentMap = {
    sky:     "from-sky-500/15 to-sky-500/0 border-sky-500/30 hover:border-sky-400/60 text-sky-300",
    emerald: "from-emerald-500/15 to-emerald-500/0 border-emerald-500/30 hover:border-emerald-400/60 text-emerald-300",
    violet:  "from-violet-500/15 to-violet-500/0 border-violet-500/30 hover:border-violet-400/60 text-violet-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`group text-left relative overflow-hidden rounded-2xl border backdrop-blur-sm p-5 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
        bg-gradient-to-br ${accentMap[accent]}`}
    >
      {beta && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border border-violet-400/40 text-violet-200 bg-violet-500/10">beta</span>}
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-lg text-slate-50">{label}</div>
          <div className="text-xs text-slate-400 mt-1 max-w-[16rem]">{sub}</div>
        </div>
        <div className="w-11 h-11 rounded-full border border-current/30 flex items-center justify-center group-hover:scale-110 transition">
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-4 text-xs text-slate-500 inline-flex items-center gap-1 group-hover:text-slate-300 transition">
        Abrir <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
}
